import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import useUserConsentController from '@/hooks/registration/useUserConsentController';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { ConsentCheckbox } from '@/components/screens/registration/consent/ConsentCheckbox';
import { ConsentRadioButton } from '@/components/screens/registration/consent/ConsentRadioButton';
import useRegisterStore, { RegistrationTrack } from '@/hooks/useRegistrationStore';
import { RegistrationSuccess } from '@/components/screens/registration/RegistrationSuccess';
import { isHtmlContent } from '@/utils/consentUtils';
import { useNavigate } from 'react-router-dom';
import { REGISTRATION_CONFIRMATION } from '@/constants/paths';
import { Sentry } from '@/main';

const UserConsentScreen = () => {
  const navigate = useNavigate();
  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [radioErrorMessage, setRadioErrorMessage] = useState<string | null>(null);

  const {
    isLoading,
    form,
    consentData,
    consentFormTitle,
    control,
    isLastStep,
    isRadioButton,
    isCheckbox,
    onClickGoBack,
    onClickNext,
    onDownloadConsentPress,
    isPendingRegistration,
  } = useUserConsentController(() => checkboxValues);
  const { registrationDone, ...registrationData } = useRegisterStore();

  const loadingForm = useForm<any>();

  useEffect(() => {
    if (!isLoading && !consentData) {
      const error = new Error('Oops! Error Fetching terms and services data.');
      Sentry.captureException(error, {
        extra: {
          ...registrationData,
        },
      });
    }
  }, [isLoading, consentData, registrationData]);

  useEffect(() => {
    if (consentData && isCheckbox) {
      const initialValues = consentData.choices?.reduce<Record<string, boolean>>((acc, choice) => {
        acc[choice.recordValue] = false;
        return acc;
      }, {}) || {};
      setCheckboxValues(initialValues);
    }
  }, [consentData, isCheckbox]);

  if (isLoading || !consentData) {
    return (
      <RegistrationScreen
        title="User Consent"
        form={loadingForm}
        content={<div>Oops! Error Fetching terms and services data.</div>}
        primaryActionLabel="Try Again"
        primaryActionOnClick={() => navigate(REGISTRATION_CONFIRMATION)}
        checkForRegisterStore={RegistrationTrack.ConsentForm}
      />
    );
  }

  const { choices, header, content } = consentData;

  const handleNextClick = () => {
    onClickNext();
  };

  const Content = (
    <div>
      {header && <h2 className="text-4xl font-sans mb-4 font-medium">{header}</h2>}
      {isHtmlContent(content) ? (
        content.length > 500 ? (
          <div className="flex flex-col items-center w-full">
            {content.includes('Introduction') && (
              <div className="w-full mb-4">
                <div
                  className="prose max-w-none [&>h1]:typography-d3"
                  dangerouslySetInnerHTML={{
                    __html: content.split('Introduction')[0],
                  }}
                />
              </div>
            )}

            <div className="w-full bg-white rounded-xl shadow-sm p-4">
              <div className="max-h-[44vh] overflow-y-auto">
                <div className="p-6 typography-body text-secondary">
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: content.includes('Introduction')
                        ? 'Introduction' + content.split('Introduction')[1]
                        : content,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mt-4 prose max-w-none"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )
      ) : (
        <div className="mt-4 prose">
          <p>{content}</p>
        </div>
      )}
      {consentData?.downloadLink && <p onClick={onDownloadConsentPress} className='text-sm font-medium leading-none underline text-gray-600 mt-4 cursor-pointer'>Download this Consent Form</p>}
      {isCheckbox && (
        <ConsentCheckbox
          choices={choices || []}
          values={checkboxValues}
          onChange={setCheckboxValues}
          onValidationChange={setIsFormValid}
        />
      )}
      {isRadioButton && (
        <ConsentRadioButton
          choices={choices || []}
          control={control}
          errorMessage={radioErrorMessage}
          onErrorChange={setRadioErrorMessage}
          onValidationChange={setIsFormValid}
          watchValue={form.watch('consentChoice')}
        />
      )}
    </div>
  );

  let isNextDisabled = isPendingRegistration;
  if (isRadioButton || isCheckbox) {
    isNextDisabled = !isFormValid || !!radioErrorMessage;
  }

  return registrationDone ? <RegistrationSuccess /> : (
    <RegistrationScreen
      title={consentFormTitle}
      form={form as any}
      content={Content}
      previousPath={REGISTRATION_CONFIRMATION}
      primaryActionIsSubmit={false}
      primaryActionLabel={isLastStep ? 'Register' : 'Next'}
      primaryActionOnClick={handleNextClick}
      primartActionDisabled={isNextDisabled}
      isLoading={isPendingRegistration}
      secondaryActionLabel="Back"
      secondaryActionOnClick={onClickGoBack}
      isConsentScreen
      checkForRegisterStore={RegistrationTrack.ConsentForm}
    />
  );
};

export default UserConsentScreen; 